/**
 * `<pg-kpi-card>` — indicador numérico compacto (KPI), reutilizável em
 * qualquer grade de indicadores (Dashboard, e futuramente resumos de
 * Pedidos, Produção, Financeiro — CODING_STANDARDS.md, seção 16: reuso
 * real por 2+ módulos justifica viver no Design System).
 *
 * O componente nunca calcula nem formata o valor — recebe `valor` já
 * formatado (ex: "R$ 4.320,00", "128") pela tela que o consome, mesma
 * regra de "desconhecimento de domínio" de `pg-table`. O mesmo vale para
 * `tendencia`/`pontos` (sparkline): o componente só desenha o que
 * recebe — nunca infere, projeta ou inventa uma tendência quando a tela
 * que o usa não tem uma série real para basear o comparativo (regra de
 * produto: nunca inventar dado para preencher a interface).
 *
 * Atributos:
 * - `label`: rótulo do indicador (ex: "Clientes").
 * - `valor`: valor já formatado para exibição.
 * - `dica`: legenda curta opcional (ex: "capado em 100+", "vs. mês anterior").
 * - `estado`: 'ok' (padrão) | 'carregando' | 'indisponivel' — quando
 *   diferente de 'ok', `valor` é ignorado e um traço neutro é mostrado
 *   no lugar, para nunca exibir número como se fosse dado real quando
 *   não é (regra de produto: nunca inventar dado).
 * - `icone`: nome de um ícone do pequeno conjunto embutido (opcional —
 *   ver `ICONES` abaixo). Puramente decorativo, nunca implica dado.
 * - `tendencia`: texto já formatado do comparativo (ex: "+12,4%",
 *   "-3,1%") — só deve ser passado quando a tela tem uma série real
 *   para calculá-lo; omitido, nenhum indicador de tendência aparece.
 * - `tendencia-direcao`: 'alta' | 'baixa' | 'neutra' — cor do texto de
 *   `tendencia` (verde/vermelho/neutro). Sem efeito se `tendencia` não
 *   for informado.
 *
 * Propriedades JS (não atributos):
 * - `pontos`: `number[]` opcional — série curta (ex: últimos 6 meses)
 *   para desenhar um sparkline minúsculo. Omitido = nenhum sparkline.
 * - `corSparkline`: cor CSS opcional (ex: `var(--pg-color-sucesso)`)
 *   para a linha do sparkline. Omitida = cor automática por tendência
 *   (verde se o último ponto ≥ o primeiro, vermelho se caiu) — o
 *   comportamento original, útil quando "subiu/caiu" É o dado principal
 *   (ex: uma série sem identidade de cor fixa própria). Quando o KPI já
 *   tem uma cor de marca associada (ex: "Lucro" = dourado), passe
 *   `corSparkline` para fixá-la, independente da tendência.
 *
 * Uso:
 * ```html
 * <pg-kpi-card label="Clientes" valor="128" dica="capado em 100+" icone="usuarios"></pg-kpi-card>
 * <pg-kpi-card label="Receita do mês" valor="R$ 12.450,00" tendencia="+8,2%" tendencia-direcao="alta" icone="dinheiro"></pg-kpi-card>
 * <pg-kpi-card label="Pedidos" estado="indisponivel"></pg-kpi-card>
 * ```
 * ```js
 * card.pontos = [8000, 9200, 8800, 10500, 11200, 12450]; // últimos 6 meses, mesma unidade do `valor`
 * ```
 */
/** Pequeno conjunto de ícones stroke embutidos — mesmo estilo visual do ícone de sino já usado em `erp-header` (24x24, stroke-width 2, sem preenchimento). Nenhuma dependência externa de ícone. */
const ICONES = {
    dinheiro: '<path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />',
    usuarios: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />',
    carrinho: '<circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />',
    producao: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 15l2 2 4-4" />',
    estoque: '<path d="M21 8V21H3V8" /><path d="M1 3h22v5H1z" /><path d="M10 12h4" />',
    parceiros: '<path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z" />',
    caixa: '<rect x="1" y="7" width="22" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />',
    tendencia: '<polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />',
    envios: '<rect x="1" y="3" width="15" height="13" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />',
    suporte: '<circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" /><path d="M4.9 4.9l4.24 4.24M14.86 14.86l4.24 4.24M14.86 9.14l4.24-4.24M4.9 19.1l4.24-4.24" />',
    garantias: '<path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11z" /><path d="M9 12l2 2 4-4" />',
    cms: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h8M8 9h2" />',
    galeria: '<rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />',
    automacoes: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />',
    bonus: '<circle cx="12" cy="8" r="7" /><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />',
};
export class PgKpiCard extends HTMLElement {
    static get observedAttributes() {
        return ['label', 'valor', 'dica', 'estado', 'icone', 'tendencia', 'tendencia-direcao'];
    }
    root;
    _pontos = [];
    _corSparkline;
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }
    get pontos() {
        return this._pontos;
    }
    set pontos(valor) {
        this._pontos = valor;
        this.render();
    }
    get corSparkline() {
        return this._corSparkline;
    }
    set corSparkline(valor) {
        this._corSparkline = valor;
        this.render();
    }
    connectedCallback() {
        this.render();
    }
    attributeChangedCallback() {
        this.render();
    }
    render() {
        const label = this.getAttribute('label') ?? '';
        const dica = this.getAttribute('dica') ?? '';
        const estado = this.getAttribute('estado') ?? 'ok';
        const icone = this.getAttribute('icone') ?? '';
        const tendencia = this.getAttribute('tendencia') ?? '';
        const tendenciaDirecao = this.getAttribute('tendencia-direcao') ?? 'neutra';
        const valorExibido = estado === 'ok' ? (this.getAttribute('valor') ?? '—') : '—';
        const dicaExibida = estado === 'indisponivel' ? dica || 'módulo não implementado' : estado === 'carregando' ? '' : dica;
        const mostrarTendencia = estado === 'ok' && tendencia;
        const mostrarSparkline = estado === 'ok' && this._pontos.length >= 2;
        this.root.innerHTML = `
      <style>
        :host { display: block; height: 100%; }
        .kpi {
          background: linear-gradient(165deg, var(--pg-color-bg-elevado, #1a1d21) 0%, color-mix(in srgb, var(--pg-color-bg-elevado, #1a1d21) 94%, black) 100%);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-lg, 16px);
          padding: var(--pg-space-5, 24px);
          font-family: var(--pg-font-familia-base, system-ui);
          height: 100%;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: var(--pg-space-2, 8px);
          box-shadow: var(--pg-shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.4));
          transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
        }
        .kpi:hover {
          border-color: color-mix(in srgb, var(--pg-color-dourado, #c9a24b) 45%, var(--pg-color-borda, #2a2f36));
          box-shadow: var(--pg-shadow-md, 0 4px 12px rgba(0, 0, 0, 0.35));
          transform: translateY(-2px);
        }
        .topo { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--pg-space-3, 12px); }
        .label {
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-semibold, 600);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
        .icone {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: var(--pg-radius-md, 10px);
          background: linear-gradient(155deg, color-mix(in srgb, var(--pg-color-dourado, #c9a24b) 20%, transparent), color-mix(in srgb, var(--pg-color-dourado, #c9a24b) 8%, transparent));
          border: 1px solid color-mix(in srgb, var(--pg-color-dourado, #c9a24b) 22%, transparent);
          color: var(--pg-color-dourado, #c9a24b);
        }
        .icone svg { width: 17px; height: 17px; }
        .rodape { display: flex; align-items: flex-end; justify-content: space-between; gap: var(--pg-space-3, 12px); }
        .valor-bloco { display: flex; flex-direction: column; gap: var(--pg-space-2, 8px); min-width: 0; }
        .valor {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-2xl, 2rem);
          font-weight: var(--pg-font-weight-bold, 700);
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.01em;
          color: var(--pg-color-texto-primario, #f2f3f5);
          line-height: var(--pg-line-height-apertado, 1.2);
        }
        .valor.opaco { color: var(--pg-color-texto-desabilitado, #5c626b); }
        .skeleton {
          width: 72px;
          height: 1.5em;
          border-radius: var(--pg-radius-sm, 6px);
          background: linear-gradient(
            90deg,
            var(--pg-color-bg-hover, #22262b) 25%,
            var(--pg-color-borda, #2a2f36) 50%,
            var(--pg-color-bg-hover, #22262b) 75%
          );
          background-size: 200% 100%;
          animation: pg-skeleton-varredura 1.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .skeleton { animation: none; }
        }
        @keyframes pg-skeleton-varredura {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .dica {
          font-size: var(--pg-font-size-xs, 0.75rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
        .dica:empty { display: none; }
        .tendencia {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-semibold, 600);
          width: fit-content;
        }
        .tendencia.alta { color: var(--pg-color-sucesso, #3bb273); }
        .tendencia.baixa { color: var(--pg-color-erro, #e5484d); }
        .tendencia.neutra { color: var(--pg-color-texto-secundario, #9aa1ab); }
        .sparkline { flex-shrink: 0; }
        .sparkline svg { display: block; }
      </style>
      <div class="kpi" part="kpi">
        <div class="topo">
          <span class="label">${escaparHtml(label)}</span>
          ${icone && ICONES[icone] ? `<span class="icone">${svgIcone(ICONES[icone])}</span>` : ''}
        </div>
        <div class="rodape">
          <div class="valor-bloco">
            ${estado === 'carregando'
            ? `<span class="skeleton" aria-hidden="true"></span>`
            : `<span class="valor ${estado === 'ok' ? '' : 'opaco'}">${escaparHtml(valorExibido)}</span>`}
            ${mostrarTendencia ? `<span class="tendencia ${tendenciaDirecao}">${svgSetaTendencia(tendenciaDirecao)}${escaparHtml(tendencia)}</span>` : `<span class="dica">${escaparHtml(dicaExibida)}</span>`}
          </div>
          ${mostrarSparkline ? `<span class="sparkline">${svgSparkline(this._pontos, this._corSparkline)}</span>` : ''}
        </div>
      </div>
    `;
    }
}
function svgIcone(pathInner) {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${pathInner}</svg>`;
}
function svgSetaTendencia(direcao) {
    if (direcao === 'alta') {
        return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="18 15 12 9 6 15" /></svg>';
    }
    if (direcao === 'baixa') {
        return '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>';
    }
    return '';
}
/**
 * Sparkline minúsculo (48x24), sem eixo/rótulo/grid/tooltip — só a forma da série. Curva levemente
 * suavizada (quadráticas passando pelos pontos médios entre vizinhos — técnica leve de propósito,
 * não a spline monótona completa de `pg-chart`: numa caixa de 48x24 o objetivo é só sugerir a
 * forma, então um arredondamento sutil já basta, sem o aparato todo).
 */
function svgSparkline(pontos, corOverride) {
    const largura = 48;
    const altura = 24;
    const minimo = Math.min(...pontos);
    const maximo = Math.max(...pontos);
    const amplitude = maximo - minimo || 1;
    const passoX = largura / (pontos.length - 1);
    const coordenadas = pontos.map((valor, i) => ({
        x: i * passoX,
        y: altura - ((valor - minimo) / amplitude) * altura,
    }));
    let caminho = `M ${coordenadas[0].x},${coordenadas[0].y}`;
    if (coordenadas.length === 2) {
        caminho += ` L ${coordenadas[1].x},${coordenadas[1].y}`;
    }
    else {
        caminho += ` L ${(coordenadas[0].x + coordenadas[1].x) / 2},${(coordenadas[0].y + coordenadas[1].y) / 2}`;
        for (let i = 1; i < coordenadas.length - 1; i++) {
            const atual = coordenadas[i];
            const proximo = coordenadas[i + 1];
            caminho += ` Q ${atual.x},${atual.y} ${(atual.x + proximo.x) / 2},${(atual.y + proximo.y) / 2}`;
        }
        const ultimo = coordenadas[coordenadas.length - 1];
        caminho += ` L ${ultimo.x},${ultimo.y}`;
    }
    const subindo = pontos[pontos.length - 1] >= pontos[0];
    const cor = corOverride ?? (subindo ? 'var(--pg-color-sucesso, #3bb273)' : 'var(--pg-color-erro, #e5484d)');
    return `<svg width="${largura}" height="${altura}" viewBox="0 0 ${largura} ${altura}" preserveAspectRatio="none"><path d="${caminho}" fill="none" stroke="${cor}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" /></svg>`;
}
function escaparHtml(valor) {
    const div = document.createElement('div');
    div.textContent = valor;
    return div.innerHTML;
}
customElements.define('pg-kpi-card', PgKpiCard);
//# sourceMappingURL=pg-kpi-card.js.map